import React, { useState } from 'react';
import { ImagePreview } from '@/components';
import {
  isPreviewableImage,
  COD_PROOF_TYPE_LABELS,
  type CodConfirmationProof,
} from '@/services/api/codOrders';

const SIZE = {
  md: { box: 'h-[104px] w-[104px]', col: 'w-[104px]', text: 'text-[11px]' },
  sm: { box: 'h-[72px] w-[72px]', col: 'w-[72px]', text: 'text-[10px]' },
} as const;

/**
 * One attachment — previewed inline when it is an image, linked when it is not.
 *
 * Shared by the detail panel's evidence block and the timeline, because both
 * answer the same question ("is there really a receipt behind this?") and a
 * link labelled `Bukti Transfer` does not answer it — it just moves the
 * question to a new tab, where it mostly stops being asked. The timeline uses
 * `size="sm"`: there the attachment sits inside an event row, so a full 104px
 * tile per file would push the narrative off screen.
 *
 * How to display is read off the URL, not assumed from `type`: a proof with no
 * extension is not a PDF, and calling it one tells the operator something false
 * about a document they are auditing. `type` says what the document IS; this
 * decides how to show it.
 */
const CodProofThumb: React.FC<{
  proof: CodConfirmationProof;
  size?: keyof typeof SIZE;
}> = ({ proof, size = 'md' }) => {
  // A stored url can stop resolving — the object gets deleted, the bucket's
  // access changes. Falling back to a link beats a broken-image icon, which
  // tells the operator nothing about whether the proof ever existed.
  const [imageFailed, setImageFailed] = useState(false);
  const label = COD_PROOF_TYPE_LABELS[proof.type] ?? proof.type;
  const s = SIZE[size];

  if (isPreviewableImage(proof.imageUrl) && !imageFailed) {
    return (
      <div className={`flex flex-col gap-1 ${s.col}`}>
        {/* A hidden probe drives the fallback: ImagePreview owns its own <img>
            and exposes no error hook, so the load is tested alongside it. */}
        <img
          src={proof.imageUrl}
          alt=""
          aria-hidden="true"
          className="hidden"
          onError={() => setImageFailed(true)}
        />
        <ImagePreview
          src={proof.imageUrl}
          alt={label}
          className={`${s.box} rounded-lg border border-stroke-soft-200 object-cover`}
        />
        <span
          className={`break-words leading-tight text-text-sub-600 ${s.text}`}
        >
          {label}
        </span>
      </div>
    );
  }

  const isPdf = /\.pdf$/.test(proof.imageUrl.split('?')[0].toLowerCase());
  return (
    <a
      href={proof.imageUrl}
      target="_blank"
      rel="noreferrer"
      className={`inline-flex items-center gap-1.5 self-start rounded-lg border border-stroke-soft-200 px-2.5 py-2 text-accent hover:border-accent hover:text-accent-dark ${
        size === 'sm' ? 'text-xs' : 'text-sm'
      }`}
    >
      <i
        className={`text-base ${
          imageFailed
            ? 'ri-image-line'
            : isPdf
              ? 'ri-file-pdf-2-line'
              : 'ri-file-line'
        }`}
        aria-hidden="true"
      />
      {imageFailed ? `${label} (gambar tidak bisa dimuat)` : label}
    </a>
  );
};

export default CodProofThumb;
